import { useEffect, useId, useMemo, useState, type MouseEvent } from "react";
import { PageHeader } from "@/components/PageHeader";
import { StateMessage } from "@/components/StateMessage";
import { demoApi, problemMessage } from "@/api/client";
import { useLabourReport } from "@/hooks/useLabourReport";
import { formatHours, formatMoney } from "@/lib/format";
import "./Labour.css";

/**
 * Hours (and an hourly-only estimated cost) per job code, over a window the farmer
 * chooses — Reports' Labour tab. ef-038-1.
 *
 * <p>
 * WHAT WAS MISSING. `job_code`/`time_entry_allocation` (ef-022) answer WHAT a day's
 * hours were for, split across as many codes as the worker chose, but nothing rolled
 * those splits UP. A farmer could see one worker's one day allocated 4h North Field / 2h
 * Shop, and had no screen that answered "how many hours, and roughly how much, went to
 * North Field this fortnight" across everyone.
 * </p>
 *
 * <p>
 * READS `time_entry_allocation` ON APPROVED ENTRIES, exactly like Hours.tsx reads
 * `time_entry` — never `employee.hours_this_period`, a single cell every `/apply` run
 * overwrites wholesale. Same reasoning, same table family, different grouping.
 * </p>
 *
 * <p>
 * THE ESTIMATED COST IS AN ESTIMATE, named as one. It prices only the HOURLY share of
 * each code's hours, at each contributing employee's own rate — a salaried employee's
 * hours still count toward the total (a roster fact), but never toward the dollar figure
 * (there is no honest per-hour price to put beside them). A code worked only by salaried
 * people shows hours with no cost at all, never a fabricated $0.00.
 * </p>
 *
 * <p>
 * THE WINDOW ON SCREEN IS THE SERVER'S, NOT THIS COMPONENT'S — same discipline as
 * Hours.tsx. `?from=&to=` in the URL seeds the FIRST request (so a link can open the
 * screen already scoped to a specific window); the picker itself is then seeded from
 * whatever the server actually reported, never a guess this client made.
 * </p>
 */
interface LabourProps {
  onNavigate: (path: string) => void;
}

export function Labour({ onNavigate }: LabourProps) {
  const initialRange = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    const from = params.get("from");
    const to = params.get("to");
    return from && to ? { from, to } : undefined;
  }, []);

  const { report, status, error, load } = useLabourReport(initialRange);

  const fromId = useId();
  const toId = useId();

  /**
   * Seeded from the server's answer, not initialised to a guess — same reasoning as
   * Hours.tsx's identical pair. Empty until the first report lands, unless the URL
   * already named a window, in which case it is on screen from the first paint.
   */
  const [from, setFrom] = useState(initialRange?.from ?? "");
  const [to, setTo] = useState(initialRange?.to ?? "");
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (report === null || touched) return;
    setFrom(report.start);
    setTo(report.end);
  }, [report, touched]);

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning Hours.tsx gives: the response carries the whole CSV as a string, and
   * paying that on every date change would make the table slower to serve a download most
   * visits never press.
   */
  const [exportState, setExportState] = useState<
    { kind: "idle" } | { kind: "busy" } | { kind: "done"; rows: number; name: string } | { kind: "error"; message: string }
  >({ kind: "idle" });

  const rangeIsBackwards = from !== "" && to !== "" && to < from;
  const canSubmit = from !== "" && to !== "" && !rangeIsBackwards && status !== "loading";

  async function downloadExport(): Promise<void> {
    if (rangeIsBackwards || from === "" || to === "") return;
    setExportState({ kind: "busy" });
    try {
      const file = await demoApi.exportLabourReport({ from, to });

      // The bytes are the server writer's output verbatim — this adds a blob and a
      // filename and nothing else, exactly as Hours.tsx's identical download does.
      // Re-serialising from rows here would make the download a second implementation
      // of the file format.
      const blob = new Blob([file.delimitedFile], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = file.delimitedFileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      setExportState({ kind: "done", rows: file.rowCount, name: file.delimitedFileName });
    } catch (err) {
      setExportState({
        kind: "error",
        message: problemMessage(err, "Could not build the labour file."),
      });
    }
  }

  const totalHours = report?.lines.reduce((sum, line) => sum + line.hours, 0) ?? 0;
  const totalCost = report?.lines.reduce((sum, line) => sum + (line.estimatedCost ?? 0), 0) ?? 0;
  const anyCost = report?.lines.some((line) => line.estimatedCost !== null) ?? false;

  /* Real <a>, same modified-click passthrough AppShell.tsx's own handleNavClick uses. */
  function handleBackClick(event: MouseEvent<HTMLAnchorElement>): void {
    if (
      event.defaultPrevented
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) {
      return;
    }
    event.preventDefault();
    onNavigate("/reports");
  }

  return (
    <main className="labour">
      {/* ef-044-1 (D2): Reports retires its tab strip for a searchable picker (ADR 0009
          §6) — this screen's way back is a plain link rather than a tab. */}
      <a href="/reports" className="labour__back" onClick={handleBackClick}>
        ← All reports
      </a>
      <PageHeader
        title="Labour by job code"
        subtitle={
          report === null
            ? "Approved hours, rolled up by what they were for"
            : `${formatWindow(report.start, report.end)} · ${report.lines.length} ${report.lines.length === 1 ? "line" : "lines"}`
        }
      />

      {/*
        data-state is the tester's ground truth (rules/ui-breadcrumbs §1): loading / error
        / empty / ready read off the DOM rather than inferred from sentence text. "empty"
        means the report resolved and the window carries no allocated (or unallocated)
        approved hours at all — a real, different answer from "the request failed".
      */}
      <section
        className="labour__panel"
        aria-labelledby="labour-heading"
        data-state={status === "ready" && (report?.lines.length ?? 0) === 0 ? "empty" : status}
      >
        <h2 id="labour-heading" className="labour__heading">
          Hours by job code
        </h2>

        <form
          className="labour__range"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canSubmit) return;
            setTouched(true);
            void load({ from, to });
          }}
        >
          <div className="labour__field">
            <label className="labour__label" htmlFor={fromId}>
              From
            </label>
            <input
              id={fromId}
              className="labour__input"
              type="date"
              value={from}
              onChange={(event) => {
                setTouched(true);
                setFrom(event.target.value);
              }}
            />
          </div>

          <div className="labour__field">
            <label className="labour__label" htmlFor={toId}>
              To
            </label>
            <input
              id={toId}
              className="labour__input"
              type="date"
              value={to}
              aria-invalid={rangeIsBackwards || undefined}
              onChange={(event) => {
                setTouched(true);
                setTo(event.target.value);
              }}
            />
          </div>

          <button type="submit" className="btn btn--primary labour__show" disabled={!canSubmit}>
            {status === "loading" ? "Loading…" : "Show labour"}
          </button>
        </form>

        {rangeIsBackwards && (
          <p className="labour__field-error" role="alert">
            The end of the period cannot be before its start.
          </p>
        )}

        {status === "loading" && <StateMessage tone="loading" title="Loading labour by job code…" />}

        {status === "error" && (
          <StateMessage
            tone="error"
            title="Could not load the labour report."
            detail={`${error ?? ""} No figures are shown — a window with nothing allocated and a report that failed to load look identical, and only one of them is a fact.`}
          />
        )}

        {status === "ready" && report !== null && report.lines.length === 0 && (
          <StateMessage
            tone="empty"
            title="Nothing allocated in this window."
            detail="Lines appear here once approved hours have been recorded and split across job codes — or left unallocated."
          />
        )}

        {status === "ready" && report !== null && report.lines.length > 0 && (
          <>
            {/* The WRAPPER scrolls, never the page — same device as Hours.tsx's table. */}
            <div className="labour__table-wrap" tabIndex={0} role="group" aria-labelledby="labour-heading">
              <table className="labour__table ruled-rows">
                <caption className="labour__caption">
                  Labour for {formatWindow(report.start, report.end)}
                </caption>
                <thead>
                  <tr>
                    <th scope="col">Code</th>
                    <th scope="col">Job</th>
                    <th scope="col" className="labour__num">
                      Hours
                    </th>
                    <th scope="col" className="labour__num">
                      Estimated cost
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {report.lines.map((line) => (
                    <tr
                      key={line.jobCodeId ?? "unallocated"}
                      /* Unallocated is a MEASUREMENT, not an error — marked as such so a
                         tester can tell it apart from a row that failed to resolve a
                         code. See LabourReportLine. */
                      data-state={line.jobCodeId === null ? "unallocated" : "allocated"}
                    >
                      <td className="labour__code">{line.code ?? "—"}</td>
                      <th scope="row" className="labour__name">
                        {line.name}
                      </th>
                      <td className="labour__num labour__hours">{formatHours(line.hours)}</td>
                      <td className="labour__num labour__amount">
                        {line.estimatedCost === null ? "—" : formatMoney(line.estimatedCost)}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td />
                    <th scope="row">Total</th>
                    <td className="labour__num labour__hours">{formatHours(totalHours)}</td>
                    <td className="labour__num labour__amount">
                      {anyCost ? formatMoney(totalCost) : "—"}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <p className="labour__disclosure">
              <strong>Estimated cost</strong> prices only the hourly share of each line, at
              each person&rsquo;s own rate — salaried hours still count toward the total
              above them, never toward the dollar figure. This is <strong>not</strong> a
              payroll ledger: no <code>pay_run</code> backs it, and it says nothing about
              overtime. <strong>Unallocated</strong> is approved time nobody has split
              across a job code yet.
            </p>
          </>
        )}

        {/*
          THE DOWNLOAD SITS OUTSIDE THE READY BRANCH ON PURPOSE — same reasoning as
          Hours.tsx's identical placement. A farmer whose report failed to load can still
          take the file; the two are separate requests, and it needs only the two dates,
          which are on screen either way.
        */}
        <div className="labour__export">
          <h3 className="labour__export-title">Labour report for your records</h3>
          <p className="labour__export-copy">
            A spreadsheet of the hours and estimated cost by job code, for the window
            above.
          </p>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={exportState.kind === "busy" || rangeIsBackwards || from === "" || to === ""}
            onClick={() => void downloadExport()}
          >
            {exportState.kind === "busy" ? "Building the file…" : "Download CSV"}
          </button>

          {exportState.kind === "done" && (
            <p className="labour__export-result" role="status">
              {/*
                THE COUNT IS THE POINT OF THIS LINE, same reasoning as Hours.tsx's own
                export result: a browser's only feedback is that a file appeared, and a
                farmer opening a header-only file cannot tell "nothing allocated in this
                window" from "something went wrong" without it stated.
              */}
              {exportState.rows === 0
                ? `Nothing allocated in this window — ${exportState.name} has its column headings and nothing else.`
                : `${exportState.rows} ${exportState.rows === 1 ? "line" : "lines"} saved as ${exportState.name}.`}
            </p>
          )}

          {exportState.kind === "error" && (
            <p className="labour__export-error" role="alert">
              {exportState.message} Nothing was downloaded.
            </p>
          )}
        </div>
      </section>
    </main>
  );
}

/**
 * "Aug 1 – Aug 15, 2026" from the server's two ISO dates — identical to Hours.tsx's own
 * helper, duplicated rather than shared for the same reason `formatHours` moved to
 * `@/lib/format` and this one-line date formatter did not: it is used in exactly two
 * places and neither is likely to want the other to change with it.
 */
function formatWindow(start: string, end: string): string {
  const asDate = (iso: string) => new Date(`${iso}T12:00:00Z`);
  const day = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone: "UTC" });
  const dayAndYear = new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
  return `${day.format(asDate(start))} – ${dayAndYear.format(asDate(end))}`;
}
